<div class="min-h-screen flex items-center justify-center bg-mist p-6">
	<div class="w-full max-w-md">
		<!-- Logo -->
		<div class="text-center mb-8">
			<app-logo [variant]="'dark'" [size]="'sm'" [isLink]="true"></app-logo>
		</div>

		<!-- Card -->
		<div class="bg-white rounded-2xl shadow-xl border border-charcoal/10 p-8">
			<!-- Verifying State -->
			@if (isVerifying()) {
			<div class="text-center py-8">
				<svg class="animate-spin h-12 w-12 text-primary mx-auto mb-4" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<p class="text-charcoal/60 font-medium">Verifying reset link...</p>
			</div>
			} @else if (showError() && !tokenValid()) {
			<!-- Invalid Token State -->
			<div class="text-center">
				<div class="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
					<svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
					</svg>
				</div>
				<h2 class="text-2xl font-bold text-charcoal mb-2">Invalid Reset Link</h2>
				<p class="text-charcoal/60 text-sm mb-6">{{ errorMessage() }}</p>
				<a routerLink="/forgot-password" class="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-75 text-mist font-semibold py-3.5 rounded-lg transition-all duration-300">
					<span>Request New Link</span>
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
					</svg>
				</a>
			</div>
			} @else {
			<!-- Valid Token - Show Form -->
			<!-- Icon -->
			<div class="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
				<svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
				</svg>
			</div>

			<h2 class="text-2xl font-bold text-charcoal mb-2">Reset Password</h2>
			<p class="text-charcoal/60 text-sm mb-6">Enter your new password below.</p>

			<!-- Success Message -->
			@if (showSuccess()) {
			<div class="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
				<div class="flex items-start gap-3">
					<svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
					</svg>
					<div>
						<p class="text-sm text-green-700">{{ successMessage() }}</p>
						<p class="text-xs text-green-600 mt-1">Redirecting to login...</p>
					</div>
				</div>
			</div>
			}

			<!-- Error Message (form validation errors) -->
			@if (showError() && tokenValid()) {
			<div class="mb-6 p-4 bg-red-50 border-l-4 border-primary rounded-lg">
				<p class="text-sm text-charcoal font-medium">{{ errorMessage() }}</p>
			</div>
			}

			<!-- Form -->
			@if (!showSuccess()) {
			<form [formGroup]="resetForm" (ngSubmit)="onSubmit()" class="space-y-5">
				<!-- New Password -->
				<div>
					<label for="password" class="block text-sm font-semibold text-charcoal mb-2">New Password</label>
					<div class="relative">
						<input
							[type]="showPassword() ? 'text' : 'password'"
							id="password"
							formControlName="password"
							[class.border-red-500]="isFieldInvalid('password')"
							class="w-full px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40 pr-12"
							placeholder="Minimum 6 characters"
						/>
						<button type="button" (click)="showPassword.set(!showPassword())" class="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/50 hover:text-charcoal transition-colors">
							@if (showPassword()) {
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
							</svg>
							} @else {
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
							</svg>
							}
						</button>
					</div>
					@if (isFieldInvalid('password')) {
					<p class="mt-1.5 text-xs text-red-500">Password must be at least 6 characters</p>
					}
				</div>

				<!-- Confirm Password -->
				<div>
					<label for="password_confirmation" class="block text-sm font-semibold text-charcoal mb-2">Confirm Password</label>
					<div class="relative">
						<input
							[type]="showConfirmPassword() ? 'text' : 'password'"
							id="password_confirmation"
							formControlName="password_confirmation"
							[class.border-red-500]="isFieldInvalid('password_confirmation')"
							class="w-full px-4 py-3 bg-white border-2 border-charcoal/20 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none text-charcoal placeholder-charcoal/40 pr-12"
							placeholder="Re-enter your password"
						/>
						<button type="button" (click)="showConfirmPassword.set(!showConfirmPassword())" class="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/50 hover:text-charcoal transition-colors">
							@if (showConfirmPassword()) {
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
							</svg>
							} @else {
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
							</svg>
							}
						</button>
					</div>
					@if (isFieldInvalid('password_confirmation')) {
					<p class="mt-1.5 text-xs text-red-500">Please confirm your password</p>
					}
				</div>

				<button
					type="submit"
					[disabled]="isSubmitting()"
					class="w-full bg-primary hover:bg-primary-75 text-mist font-semibold py-3.5 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2">
					@if (isSubmitting()) {
					<svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<span>Resetting...</span>
					} @else {
					<span>Reset Password</span>
					}
				</button>
			</form>
			}

			@if (showSuccess()) {
			<a routerLink="/login" class="w-full mt-4 inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-75 text-mist font-semibold py-3.5 rounded-lg transition-all duration-300">
				<span>Go to Login</span>
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
				</svg>
			</a>
			}
			}
		</div>
	</div>
</div>
